import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createFolder, deleteFolder, listActivity, listFolderItems, listFolders, removeFolderItem, updateFolder,
  type ActivityItem, type FolderItem, type TeamFolder,
} from '../../api/collaborationApi';
import styles from './TeamFoldersPage.module.css';

/** Shared folders (SRS #73) and the workspace activity feed (SRS #70). */

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const who = (p: { name: string | null; email: string | null }) => p.name || p.email || 'Someone';
const linkFor = (type: string, ref: string) => (type === 'regulation' ? `/regulations/${ref}` : type === 'document' ? '/enterprise/dashboard/repository' : '#');

export default function TeamFoldersPage() {
  const [folders, setFolders] = useState<TeamFolder[]>([]);
  const [isOwner, setIsOwner] = useState(false);
  const [selected, setSelected] = useState<TeamFolder | null>(null);
  const [items, setItems] = useState<FolderItem[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, a] = await Promise.all([listFolders(), listActivity(30)]);
      setFolders(f.folders); setIsOwner(f.is_owner); setActivity(a.items);
    } catch (e) {
      setUnavailable(true);
      setError(e instanceof Error ? e.message : 'Collaboration is not available');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setItems([]); return; }
    listFolderItems(selected.id).then((r) => setItems(r.items)).catch(() => setItems([]));
  }, [selected]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
      if (selected) listFolderItems(selected.id).then((r) => setItems(r.items)).catch(() => {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    }
  };

  if (unavailable) return <div className={styles.page}><p className={styles.error}>{error}</p></div>;

  const roots = folders.filter((f) => f.parent_id === null);
  const children = (id: number) => folders.filter((f) => f.parent_id === id);

  return (
    <div className={styles.page}>
      <div className={styles.columns}>
        <div className={styles.col}>
          <h2 className={styles.h2}>Shared folders</h2>
          {error && <p className={styles.error}>{error}</p>}
          <form className={styles.newRow} onSubmit={(e) => { e.preventDefault(); if (name.trim()) act(() => createFolder({ name, parent_id: selected?.id ?? null }).then(() => setName(''))); }}>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={selected ? `New folder inside "${selected.name}"` : 'New folder name'} />
            <button type="submit" disabled={!name.trim()}>Create</button>
          </form>
          <ul className={styles.tree}>
            {roots.length === 0 && <li className={styles.empty}>No folders yet. Create one, then add regulations from any regulation page.</li>}
            {roots.map((f) => (
              <li key={f.id}>
                <button className={selected?.id === f.id ? styles.folderActive : styles.folder} onClick={() => setSelected(selected?.id === f.id ? null : f)}>
                  📁 {f.name} <small>({f.item_count ?? 0})</small>
                </button>
                {children(f.id).length > 0 && (
                  <ul className={styles.subtree}>
                    {children(f.id).map((c) => (
                      <li key={c.id}><button className={selected?.id === c.id ? styles.folderActive : styles.folder} onClick={() => setSelected(c)}>📁 {c.name} <small>({c.item_count ?? 0})</small></button></li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.col}>
          {selected ? (
            <>
              <div className={styles.folderHead}>
                <h2 className={styles.h2}>{selected.name}</h2>
                <span className={styles.actions}>
                  <button onClick={() => { const n = window.prompt('Rename folder', selected.name); if (n && n.trim()) act(() => updateFolder(selected.id, { name: n.trim() }).then((f) => setSelected(f))); }}>Rename</button>
                  <button onClick={() => { if (window.confirm(`Delete "${selected.name}" and everything in it?`)) act(() => deleteFolder(selected.id).then(() => setSelected(null))); }}>Delete</button>
                </span>
              </div>
              <p className={styles.muted}>Created by {who(selected.created_by)} · {when(selected.created_at)}{isOwner ? '' : ' · only the creator or the workspace owner can rename or delete'}</p>
              <ul className={styles.items}>
                {items.length === 0 && <li className={styles.empty}>Empty. Open a regulation and use "Shared folders → Add here".</li>}
                {items.map((i) => (
                  <li key={i.id} className={styles.itemRow}>
                    <span className={styles.kind}>{i.item_type}</span>
                    <Link to={linkFor(i.item_type, i.item_ref)}>{i.title || i.item_ref}</Link>
                    <span className={styles.muted}>{who(i.added_by)} · {when(i.added_at)}</span>
                    <button onClick={() => act(() => removeFolderItem(selected.id, i.id))} aria-label="Remove">×</button>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <>
              <h2 className={styles.h2}>Team activity</h2>
              <ul className={styles.items}>
                {activity.length === 0 && <li className={styles.empty}>Nothing yet. Notes, comments and folder additions from your team appear here.</li>}
                {activity.map((a) => (
                  <li key={`${a.kind}-${a.id}`} className={styles.itemRow}>
                    <span className={styles.kind}>{a.kind === 'note' ? 'Note' : a.kind === 'comment' ? 'Comment' : 'Filed'}</span>
                    <Link to={linkFor(a.target_type, a.target_ref)}>{a.preview}</Link>
                    <span className={styles.muted}>{who(a.author)} · {when(a.at)}</span>
                    <span />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
